/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeMultiFieldSubExpressions } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { ExtensionGroupOrderingOptions, ExtensionGroupRegisterOptions } from '../models/extension-register-options';
import { ExtensionGroupsFilterOption } from '../models/extensions-group-filter-option';
import { getExtensionGroupsQuery } from '../queries/extensionGroups.graphql';
import { GetExtensionGroups, GetExtensionGroupsVariables } from '../queries/generated/GetExtensionGroups';

@Injectable({ providedIn: 'root' })
export class ExtensionGroupRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getExtensionGroups(
      endCursor: string | null,
      filters: ExtensionGroupsFilterOption[] | null,
      quickFilters: ExtensionGroupRegisterOptions | null,
      orderingOptions: ExtensionGroupOrderingOptions[] | null
   ): Observable<ExecutionResult<GetExtensionGroups>> {
      const filterBuilderFilterExpression: FilterExpression = { filterOptions: [] };
      if (filters && filters.length > 0) {
         filterBuilderFilterExpression.filterOptions = filters.map(this.convertExtensionGroupsFilterOption.bind(this));
      }

      if (quickFilters && quickFilters.searchText) {
         const searchWords = quickFilters.searchText.trim().split(/\s+/);
         const fieldsToSearch = ['Name', 'Description'];

         filterBuilderFilterExpression.subExpressions = searchWords.map((word) => {
            const wordSubExpression: FilterExpression = {
               filterOptions: fieldsToSearch.map((field) => {
                  return {
                     field: field,
                     operation: FilterOperations.Contains,
                     value: word,
                  };
               }),
               logicalOperator: LogicalOperators.Or,
            };

            return wordSubExpression;
         });

         filterBuilderFilterExpression.logicalOperator = LogicalOperators.And;
      }
      const variables = this.getExtensionGroupsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetExtensionGroups, GetExtensionGroupsVariables>({
         query: getExtensionGroupsQuery,
         variables,
      });
   }

   private convertExtensionGroupsFilterOption(option: ExtensionGroupsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(filterOption: ExtensionGroupsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (filterOption.operation === FilterOperations.HasValue) {
         return filterOption.value;
      }

      switch (filterOption.type) {
         case 'extensionGroup':
            return Array.isArray(filterOption.value)
               ? filterOption.value.map((v) => {
                    return v.extensionGroupId;
                 })
               : filterOption.value?.extensionGroupId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return filterOption.value;
      }
   }

   private getExtensionGroupsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetExtensionGroupsVariables {
      const variables: GetExtensionGroupsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string | null {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeMultiFieldSubExpressions({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeMultiFieldSubExpressions({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeMultiFieldSubExpressions(expressions[0]);
      } else {
         return '';
      }
   }
}
